import { useTracker } from '@/contexts/tracker-context';

interface SettingToggleProps {
  label: string;
  checked: boolean;
  onChange: () => void;
}

function SettingToggle({ label, checked, onChange }: SettingToggleProps) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer group">
      <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{label}</span>
      <button
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`relative w-10 h-5 rounded-full transition-colors duration-200
          ${checked ? 'bg-zelda-gold' : 'bg-gray-700'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200
            ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
    </label>
  );
}

interface NumberSettingProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

function NumberSetting({ label, value, onChange, min = 0, max = 20 }: NumberSettingProps) {
  return (
    <label className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-gray-300">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Math.max(min, Math.min(max, parseInt(e.target.value) || 0)))}
        className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-sm text-center text-white focus:border-zelda-gold focus:outline-none"
        min={min}
        max={max}
      />
    </label>
  );
}

export function SettingsPage() {
  const { state, toggleSetting, setSetting, reset } = useTracker();

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-zelda-gold">Tracker Settings</h1>
        <button
          onClick={reset}
          className="px-4 py-2 bg-red-900/30 hover:bg-red-900/50 text-red-400 hover:text-red-300 rounded-lg border border-red-900/30 transition-colors text-sm"
        >
          Reset All
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Global */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zelda-gold uppercase tracking-wider mb-3">Global</h2>
          <div className="space-y-1">
            <SettingToggle label="Way of the Hero" checked={state.isWoth} onChange={() => toggleSetting('isWoth')} />
            <SettingToggle label="Item Tracker" checked={state.useItemTracker} onChange={() => toggleSetting('useItemTracker')} />
            <SettingToggle label="Reduced Items" checked={state.useItemTrackerReduce} onChange={() => toggleSetting('useItemTrackerReduce')} />
            <NumberSetting label="# Goals" value={state.nbGoals} onChange={(v) => setSetting('nbGoals', v)} max={10} />
            <NumberSetting label="# Sometimes" value={state.nbSometimes} onChange={(v) => setSetting('nbSometimes', v)} />
            <NumberSetting label="# Barren" value={state.nbBarren} onChange={(v) => setSetting('nbBarren', v)} />
          </div>
        </section>

        {/* Dungeons & Bosses */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zelda-gold uppercase tracking-wider mb-3">Dungeons & Bosses</h2>
          <div className="space-y-1">
            <SettingToggle label="Dungeon Shuffle" checked={state.isDungeonShuffle} onChange={() => toggleSetting('isDungeonShuffle')} />
            <SettingToggle label="Boss Shuffle" checked={state.isBossShuffle} onChange={() => toggleSetting('isBossShuffle')} />
            <SettingToggle label="Trials" checked={state.useTrials} onChange={() => toggleSetting('useTrials')} />
          </div>
        </section>

        {/* Spawn & Warps */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zelda-gold uppercase tracking-wider mb-3">Spawn & Warps</h2>
          <div className="space-y-1">
            <SettingToggle label="Random Spawn" checked={state.isSpawnRandom} onChange={() => toggleSetting('isSpawnRandom')} />
            <SettingToggle label="Warp Song Random" checked={state.isWarpSongRandom} onChange={() => toggleSetting('isWarpSongRandom')} />
            <SettingToggle label="Owl Random" checked={state.isOwlRandom} onChange={() => toggleSetting('isOwlRandom')} />
          </div>
        </section>

        {/* Mix Pool */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zelda-gold uppercase tracking-wider mb-3">Mix Pool</h2>
          <div className="space-y-1">
            <SettingToggle label="Grotto" checked={state.isMixedPoolGrotto} onChange={() => toggleSetting('isMixedPoolGrotto')} />
            <SettingToggle label="Interior" checked={state.isMixedPoolInterior} onChange={() => toggleSetting('isMixedPoolInterior')} />
            <SettingToggle label="Dungeon" checked={state.isMixedPoolDungeon} onChange={() => toggleSetting('isMixedPoolDungeon')} />
            <SettingToggle label="Overworld" checked={state.isMixedPoolOverworld} onChange={() => toggleSetting('isMixedPoolOverworld')} />
          </div>
        </section>

        {/* Sanity */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zelda-gold uppercase tracking-wider mb-3">Sanity Settings</h2>
          <div className="space-y-1">
            <SettingToggle label="Scrubsanity" checked={state.isScrubsanity} onChange={() => toggleSetting('isScrubsanity')} />
            <SettingToggle label="Cowsanity" checked={state.isCowsanity} onChange={() => toggleSetting('isCowsanity')} />
            <SettingToggle label="Shopsanity" checked={state.useShopsanity} onChange={() => toggleSetting('useShopsanity')} />
            <SettingToggle label="Keysanity" checked={state.isKeysanity} onChange={() => toggleSetting('isKeysanity')} />
            <SettingToggle label="Key Ring" checked={state.isKeyring} onChange={() => toggleSetting('isKeyring')} />
            <SettingToggle label="Enemy Soul" checked={state.isEnnemySoul} onChange={() => toggleSetting('isEnnemySoul')} />
          </div>
        </section>

        {/* Multiworld */}
        <section className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-zelda-gold uppercase tracking-wider mb-3">Multiworld</h2>
          <div className="space-y-1">
            <SettingToggle label="Multiworld" checked={state.isMultiworld} onChange={() => toggleSetting('isMultiworld')} />
            {state.isMultiworld && (
              <NumberSetting
                label="# Players"
                value={state.nbMultiworldPlayer}
                onChange={(v) => setSetting('nbMultiworldPlayer', v)}
                min={2}
                max={8}
              />
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
